import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { projectTasksPath } from 'utils/projectPath'
import moment from 'moment'
import { ChevronRight, MapPin, MessageCircle, GitBranch, ListTodo, Users } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'

import Layout from 'components/Layout'
import Card from 'components/Card'
import { ChatHeaderListContainer, useChatPopup } from 'features/Chat'
import { initializeActiveLocations } from 'slices/locationSlice'
import { initializePortfolios } from 'slices/portfolioSlice'
import { initializeAggregatedTasks, initializeAssignedTaskCount } from 'slices/tasksSlice'
import { initializeMyProjects } from 'slices/projectSlice'
import { initializeMyPartners } from 'slices/myPartnersSlice'

import { useChat, ChatHeaderList } from 'features/Chat'
import { getChatHeadersByUID } from 'slices/chatSlice'

import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'

const HomeGuest = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	// The global chat is a popup, not a page. Chat affordances open it here.
	const { open: openChat } = useChatPopup()

	const [taskListIsPastDue, setTaskListIsPastDue] = useState(false)

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo: user } = userLogin
	const userId = user._id

	const { entities: projectEntities, ids: projectIds } = useSelector((state: any) => state.projects)
	const projects = projectIds.map(id => projectEntities[id])

	const { entities: chats, ids: chatsds, isInitialized: chatsInitialized } = useSelector(
		(state: any) => state.chatHeaders,
	)
	const chatHeaders = chatsds.map(id => chats[id])

	const {
		entities: locations,
		ids: locationIds,
		isInitialized: locationsInitialized,
	} = useSelector((state: any) => state.locations)
	const locationsByDays = locationIds.map(id => locations[id])

	const {
		entities: portfolios,
		ids: portfolioIds,
		isInitialized: portfoliosInitialized,
	} = useSelector((state: any) => state.portfolios)
	const portfoliosByDays = portfolioIds.map(id => portfolios[id])

	const {
		entities: tasks,
		ids: taskIds,
		isLoading: tasksLoading,
		isInitialized: tasksInitialized,
	} = useSelector((state: any) => state.tasks)
	const aggregatedTasks = taskIds.map(id => tasks[id])

	const { taskCount: assignedTasks } = useSelector((state: any) => state.tasks.assignedTaskCount)

	const {
		entities: myPartners,
		ids: myPartnerIds,
		isInitialized: myPartnersInitialized,
	} = useSelector((state: any) => state.myPartners)
	const myPartnersList = myPartnerIds.map(id => myPartners[id])

	useEffect(() => {
		if (!chatsInitialized) {
			dispatch(getChatHeadersByUID())
		}
		if (!locationsInitialized) {
			dispatch(initializeActiveLocations())
		}

		if (!myPartnersInitialized) {
			dispatch(initializeMyPartners())
		}

		if (!portfoliosInitialized) {
			dispatch(initializePortfolios())
		}

		dispatch(initializeMyProjects())
		dispatch(initializeAssignedTaskCount({ userId: userId }))

		if (taskListIsPastDue) {
			dispatch(
				initializeAggregatedTasks({
					userId: userId,
					backDays: 1000,
					forwardDays: 0,
				}),
			)
		} else {
			dispatch(
				initializeAggregatedTasks({
					userId: userId,
					backDays: 0,
					forwardDays: 30,
				}),
			)
		}
	}, [
		dispatch,
		myPartnersInitialized,
		user,
		userId,
		taskListIsPastDue,
		locationsInitialized,
		portfoliosInitialized,
	])

	const CompletedDays = ({ completedBy }) => {
		const timezoneOffset = new Date().getTimezoneOffset() / 60
		const hoursOut = moment(completedBy).add(timezoneOffset, `hours`).diff(moment(), 'hours')
		let daysOut = moment(completedBy).add(timezoneOffset, `hours`).diff(moment(), 'days')

		if (hoursOut < 0) {
			daysOut = daysOut - 1
		}

		const daysLabel = daysOut > 1 || daysOut < -1 ? `days` : `day`
		const result = daysOut === 0 ? 'Today' : `${daysOut} ${daysLabel}`

		return <div className={daysOut < 0 ? 'text-red-500' : ''}>{result}</div>
	}


	return (
		<Layout>
			<Layout.Page>
				<Layout.Sidebar>
					<Layout.SidebarUserCard />
				</Layout.Sidebar>
				<Layout.Body>
					<div className="grid grid-cols-12 gap-6">
						{projects.length > 0 ? (
							<Card
								className="border-t-4 border-[#25a4fc] min-h-[360px] col-span-8"
								actions={<Link to="/projects">View all projects &rarr;</Link>}
							>
								<div className="flex items-center justify-between mb-4">
									<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">
										<GitBranch className="w-5 h-5" /> Projects
									</h2>
								</div>
								<div className="h-full max-h-[240px] overflow-y-auto">
									{projects && projects.length > 0 ? (
										<ul className="list-none p-0 m-0">
											{projects.map((project, index) => (
												<li key={index}>
													<Link
														to={projectTasksPath(project)}
														className="flex items-center gap-3 px-2 py-1.5 no-underline text-inherit hover:bg-gray-50 rounded transition-colors"
													>
														<Avatar className="h-10 w-10 flex-shrink-0">
															<AvatarImage src={project.projectAsset?.image} />
															<AvatarFallback />
														</Avatar>
														<div className="flex-1 min-w-0">
															<p className="m-0 text-sm font-medium truncate">
																{project.projectAsset?.assetName}: {project.projectName}
															</p>
															<p className="m-0 text-xs text-gray-500 truncate">{project.projectDescription}</p>
														</div>
														<ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
													</Link>
												</li>
											))}
										</ul>
									) : null}
								</div>
							</Card>
						) : (
							<Card
								className="border-t-4 border-[#25a4fc] min-h-[360px] col-span-8"
								actions={<Link to="/locations">Create a new Project &rarr;</Link>}
							>
								<div className="flex items-center justify-between mb-4">
									<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">
										<GitBranch className="w-5 h-5" /> Projects
									</h2>
								</div>
								<div className="flex items-center justify-center min-h-[246px] h-full">
									<Link to="/locations" className="no-underline">
										<h3 className="text-center text-[22px] font-normal text-[#25a4fc] px-5 hover:font-medium cursor-pointer">
											Add Projects to Locations to manage Documents, Images, Notes, etc.
										</h3>
									</Link>
								</div>
							</Card>
						)}
						{chatHeaders.length > 0 ? (
							<Card
								className="border-t-4 border-[#25a4fc] min-h-[360px] col-start-9 col-span-4"
								actions={
									<button type="button" onClick={() => openChat()} className="text-[#25a4fc]">
										View all chats &rarr;
									</button>
								}
							>
								<div className="flex items-center justify-between mb-4">
									<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">
										<MessageCircle className="w-5 h-5" /> Chats
									</h2>
									<p className="m-0 text-[11px] italic">Most recent</p>
								</div>
								<div className="h-full max-h-[240px] overflow-y-auto">
									<ChatHeaderListContainer isLink showSelected={false} />
								</div>
							</Card>
						) : (
							<Card
								className="border-t-4 border-[#25a4fc] min-h-[360px] col-start-9 col-span-4"
								actions={
									myPartnersList.length > 0 && (
										<button
											type="button"
											onClick={() => openChat({ compose: true })}
											className="text-[#25a4fc]"
										>
											Start a new chat &rarr;
										</button>
									)
								}
							>
								<div className="flex items-center justify-between mb-4">
									<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">
										<MessageCircle className="w-5 h-5" /> Chats
									</h2>
								</div>
								<div className="flex items-center justify-center min-h-[246px] h-full">
									<button type="button" onClick={() => openChat()} className="no-underline">
										<h3 className="text-center text-[22px] font-normal text-[#25a4fc] px-5 hover:font-medium cursor-pointer">
											Connect with one or more Partners and interact with them via Chat.
										</h3>
									</button>
								</div>
							</Card>
						)}
						{assignedTasks > 0 ? (
							<Card
								className="border-t-4 border-[#25a4fc] min-h-[360px] col-span-8"
								actions={<Link to="/taskscn">View all tasks &rarr;</Link>}
							>
								<div className="flex items-center justify-between mb-4">
									<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">
										<ListTodo className="w-5 h-5" /> Tasks
									</h2>
									<div className="inline-flex rounded-md shadow-sm">
										<button
											type="button"
											className={`px-3 py-1 text-sm font-medium rounded-l-md border border-blue-500 ${
												!taskListIsPastDue
													? 'bg-blue-500 text-white'
													: 'bg-white text-blue-500 hover:bg-blue-50'
											}`}
											onClick={() => setTaskListIsPastDue(false)}
										>
											Pending
										</button>
										<button
											type="button"
											className={`px-3 py-1 text-sm font-medium rounded-r-md border border-blue-500 border-l-0 ${
												taskListIsPastDue
													? 'bg-blue-500 text-white'
													: 'bg-white text-blue-500 hover:bg-blue-50'
											}`}
											onClick={() => setTaskListIsPastDue(true)}
										>
											Past Due
										</button>
									</div>
								</div>
								<div className="h-full max-h-[240px] overflow-y-auto">
									{!tasksLoading && (
										<>
											{tasksInitialized ? (
												<Table>
													<TableHeader>
														<TableRow className="border-b-2 border-[#027fff] hover:bg-transparent">
															<TableHead className="text-gray-700 text-xs font-semibold">Task</TableHead>
															<TableHead className="text-gray-700 text-xs font-semibold">Location</TableHead>
															<TableHead className="text-gray-700 text-xs font-semibold text-right">Due</TableHead>
														</TableRow>
													</TableHeader>
													<TableBody>
														{aggregatedTasks.map(task => (
															<TableRow
																className="h-7 hover:bg-[#fffbf2] cursor-pointer no-underline"
																key={task._id}
																onClick={() => navigate(`/locations/${task.packageid._id}/projects/${task.projectid?._id || task.projectid}/tasks`)}
															>
																<TableCell className="py-1 text-sm">
																	{task.taskName}
																</TableCell>
																<TableCell className="py-1 text-sm">
																	{task.packageid ? (
																		task.packageid.assetName
																	) : task.portfolioid ? (
																		<Link to={`/portfolios/${task.portfolioid._id}/tasks`}>
																			{task.portfolioid.assetName}
																		</Link>
																	) : null}
																</TableCell>
																<TableCell className="py-1 text-sm text-right">
																	<CompletedDays completedBy={task.completedBy} />
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											) : (
												<div className="flex items-center justify-center min-h-[246px] h-full">
													<h3 className="text-base font-light text-gray-500">No Tasks</h3>
												</div>
											)}
										</>
									)}
								</div>
							</Card>
						) : (
							<Card
								className="border-t-4 border-[#25a4fc] min-h-[360px] col-span-8"
								actions={<Link to="/taskscn">View all tasks &rarr;</Link>}
							>
								<div className="flex items-center justify-between mb-4">
									<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">
										<ListTodo className="w-5 h-5" /> Tasks
									</h2>
								</div>
								<div className="flex items-center justify-center min-h-[246px] h-full">
									{projects && projects.length > 0 ? (
										<div>
											<h3 className="text-center text-[22px] font-normal text-[#25a4fc] px-5 hover:font-medium cursor-pointer">
												Select a Project below to create an initial Task.
											</h3>
											<ul className="list-none p-0 m-0">
												{projects.map((project, index) => (
													<li key={index}>
														<Link
															to={projectTasksPath(project)}
															className="flex items-center gap-3 px-2 py-1.5 no-underline text-inherit hover:bg-gray-50 rounded transition-colors"
														>
															<Avatar className="h-10 w-10 flex-shrink-0">
																<AvatarImage src={project.projectAsset?.image} />
																<AvatarFallback />
															</Avatar>
															<div className="flex-1 min-w-0">
																<p className="m-0 text-sm font-medium truncate">
																	{project.projectAsset?.assetName}: {project.projectName}
																</p>
																<p className="m-0 text-xs text-gray-500 truncate">{project.projectDescription}</p>
															</div>
															<ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
														</Link>
													</li>
												))}
											</ul>
										</div>
									) : (
										<Link to="/locations" className="no-underline">
											<h3 className="text-center text-[22px] font-normal text-[#25a4fc] px-5 hover:font-medium cursor-pointer">
												Create and assign Tasks to Projects within a Location.
											</h3>
										</Link>
									)}
								</div>
							</Card>
						)}
					</div>
				</Layout.Body>
			</Layout.Page>
		</Layout>
	)
}

export default HomeGuest
